import clsx from "clsx";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CopyToClipboard, RenderWhen } from "../../../../../components";
import { route } from "../../../../../shim/jsbridge";
import { BreadcrumbItem } from "../../../@types/breadcrumb";
import useGetRelationshipPaths from "../../../hooks/useGetRelationshipPaths";
import NavBreadcrumb from "../../NavBreadcrumb";
import DroppedFilesNavBreadcrumb from "../DroppedFilesNavBreadcrumb";

type SampleSummaryBreadcrumbProps = {
  filename: string;
  sourceFilename: string;
  relationshipId: number | string;
  sourceId: number | string;
  sourceSha1: string;
  className?: string;
};

const SampleSummaryBreadcrumb: React.FC<SampleSummaryBreadcrumbProps> = ({
  filename,
  sourceFilename,
  relationshipId,
  sourceId,
  sourceSha1,
  className
}) => {
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const droppedSourceId = searchParams.get("dropped_source_id");
  const navigate = useNavigate();

  const handleBreadcrumbItemClick = ({
    value: { hashValue, r, path }
  }: {
    value: BreadcrumbItem;
  }) => {
    const sampleDetailPage = `${route("samples_details_main", {
      hash_value: hashValue || sourceSha1,
      source_id: sourceId
    })}extracted-files/`;

    if (r !== 0) {
      navigate(
        `${sampleDetailPage}?r=${r}&path=${window.encodeURIComponent(path)}`
      );
    } else {
      navigate(`${sampleDetailPage}?path=${window.encodeURIComponent(path)}`);
    }
  };

  const { paths } = useGetRelationshipPaths(sourceSha1, {
    r: relationshipId,
    hashValue: sourceSha1
  });

  return (
    <>
      <RenderWhen condition={Boolean(droppedSourceId)}>
        <DroppedFilesNavBreadcrumb
          filename={filename}
          droppedSourceId={droppedSourceId ?? ""}
          className={className}
        />
      </RenderWhen>
      <RenderWhen condition={paths.length > 0 && !droppedSourceId}>
        <NavBreadcrumb
          className={clsx("breadcrumbs-nav", className)}
          innerBreadcrumbClassName={"report-summary-breadcrumb"}
          sourceFileName={sourceFilename}
          relationshipPaths={paths}
          sampleRelationshipID={relationshipId}
          sourceHash={sourceSha1}
          onItemClick={handleBreadcrumbItemClick}
          data-testid="report_summary_header_breadcrumb"
        />
      </RenderWhen>
      <RenderWhen condition={!droppedSourceId && paths.length === 0}>
        <div className={"flex align-center"}>
          <span
            className={"overflow-ellipsis"}
            data-testid="report_summary_top_header_filename"
          >
            {filename ?? sourceSha1}
          </span>
          <CopyToClipboard
            value={filename ?? sourceSha1}
            containerClassName="ml-2"
            setContainerLineHeight={false}
          />
        </div>
      </RenderWhen>
    </>
  );
};

export default SampleSummaryBreadcrumb;
